import { Link } from 'react-router-dom';
import { useInfiniteTranscriptions } from '../hooks/queries/useTranscriptions';
import { formatUsageHours, planLabel, transcriptionTitle } from '../utils/formatters';
import { Spinner, ErrorState, Card, Badge, Button } from '../components/ui';
import { statusToBadgeVariant, statusToLabel } from '../utils/statusBadge';
import { linkButtonClass } from '../utils/linkButtonClass';
import {
  extraSessionsNote,
  isSessionLimitExempt,
  sessionGuidance,
  SESSION_GUIDANCE_MESSAGE,
} from '../utils/sessionQuota';
import { BILLING_ENABLED } from '../config/featureFlags';
import Layout from '../components/Layout';
import Header from '../components/Header';
import type { User } from '../types';

interface DashboardPageProps {
  user: User;
}

export default function DashboardPage({ user }: DashboardPageProps) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteTranscriptions(20);
  // 読み込み済みページを連結して表示する（「もっと見る」で 20 件ずつ積み増す）
  const transcriptions = data?.pages.flatMap((p) => p.transcriptions) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  // 上限は購入した追加時間を含む。-1（無制限センチネル）はそのまま渡し、表記は formatUsageHours に委ねる
  const hoursLimit =
    user.plan.hours_limit === -1 ? -1 : user.plan.hours_limit + (user.plan.extra_hours ?? 0);

  // セッション回数の表示。-1 は無制限。月枠の時間を使い切り追加時間で書き起こす間は
  // バックエンドが回数上限を適用しないため、実質「回数無制限」であることを明示する
  // （時間を買い足したのに回数切れで書き起こせない、という誤解＝サポート問い合わせを防ぐ）。
  const sessionsLimit = user.plan.sessions_limit;
  const sessionsExempt = sessionsLimit !== -1 && isSessionLimitExempt(user.plan);
  // 回数切れで月枠の時間が残っているときの案内。課金機能オフでは購入もアップグレードもできないので出さない
  const guidance = BILLING_ENABLED ? sessionGuidance(user.plan) : null;

  return (
    <Layout>
      <Header user={user} />

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">ダッシュボード</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <div className="text-sm text-gray-600">使用時間</div>
              <div className="text-xl md:text-2xl font-bold text-primary">
                {formatUsageHours(user.plan.hours_used)} / {formatUsageHours(hoursLimit)}
              </div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600">セッション回数</div>
              <div className="text-xl md:text-2xl font-bold text-primary">
                {sessionsLimit === -1
                  ? '無制限'
                  : `${user.plan.sessions_used} / ${sessionsLimit}回${extraSessionsNote(user.plan)}`}
              </div>
              {sessionsExempt && (
                <div className="text-xs text-gray-500 mt-1">
                  追加時間があるため回数無制限で書き起こせます
                </div>
              )}
              {guidance && (
                <div className="text-sm text-gray-700 mt-2">
                  <p>{SESSION_GUIDANCE_MESSAGE[guidance]}</p>
                  <Link to="/billing" className="text-primary hover:underline">
                    {guidance === 'buy_sessions' ? '回数を購入する' : 'プランを見る'}
                  </Link>
                </div>
              )}
            </Card>
            <Card>
              <div className="text-sm text-gray-600">プラン</div>
              <div className="text-xl md:text-2xl font-bold text-primary">
                {planLabel(user.plan.plan_type)}
              </div>
            </Card>
          </div>
        </div>

        <Card>
          <div
            data-testid="dashboard-history-header"
            className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-20 mb-6"
          >
            <h3 className="text-xl font-bold">書き起こし履歴</h3>
            <Link to="/upload" className={linkButtonClass('primary')}>
              新規アップロード
            </Link>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : isError ? (
            <ErrorState message="書き起こし履歴の読み込みに失敗しました" onRetry={() => refetch()} />
          ) : transcriptions.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              まだ書き起こしがありません
            </div>
          ) : (
            <div className="space-y-4">
              {transcriptions.map((t) => (
                <Link
                  key={t.id}
                  to={`/transcription/${t.id}`}
                  className="block p-4 border rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold">{transcriptionTitle(t)}</h4>
                      <p className="text-sm text-gray-600">
                        {new Date(t.created_at).toLocaleString('ja-JP')}
                      </p>
                    </div>
                    <Badge variant={statusToBadgeVariant(t.status)}>
                      {statusToLabel(t.status)}
                    </Badge>
                  </div>
                </Link>
              ))}

              {/* 全件を一度に返すと重くなるため 20 件ずつ。残りがある間だけ追加読み込み導線を出す */}
              {hasNextPage && (
                <div className="pt-2 text-center">
                  <Button
                    variant="secondary"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? '読み込み中...' : 'もっと見る'}
                  </Button>
                  <p className="mt-2 text-xs text-gray-500">
                    {transcriptions.length} / {total} 件を表示中
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>
      </main>
    </Layout>
  );
}
